import apiClient from './apiClient';
import { JobRole, ExperienceLevel, Language, InterviewGoal } from './configService';
import { ResumeProtocol } from '../types/resume'; // Assuming ResumeProtocol type is defined in this file

// Define types for career preferences
export interface CareerPreferencesCreateRequest {
  preferredJobRoles: string[];
  experienceLevelId: string;
  languageIds: string[];
  interviewGoalIds: string[];
  preferredLocations?: string[];
  salaryExpectation?: {
    minimum: number;
    maximum: number;
    currency: string;
  };
  workArrangements?: string[];
  skillsOfInterest?: string[];
  openToOpportunities?: boolean;
}

// UI format for career preferences (used in the form)
export interface CareerPreferencesUIFormat {
  jobRoles: string[];
  experienceLevel: string;
  languages: string[];
  interviewGoals: string[];
  preferredLocations?: string[];
  salaryExpectation?: string;
  workArrangements?: string[];
  skillsOfInterest?: string[];
  openToOpportunities?: boolean;
}

// Configuration data response format
export interface ConfigDataResponse {
  jobRoles: JobRole[];
  experienceLevels: ExperienceLevel[];
  languages: Language[];
  interviewGoals: InterviewGoal[];
}

export interface CareerPreferencesUpdateRequest {
  preferredJobRoles?: string[];
  experienceLevelId?: string;
  languageIds?: string[];
  interviewGoalIds?: string[];
  preferredLocations?: string[];
  salaryExpectation?: {
    minimum: number;
    maximum: number;
    currency: string;
  };
  workArrangements?: string[];
  skillsOfInterest?: string[];
  openToOpportunities?: boolean;
}

export interface CareerPreferencesResponse {
  _id: string;
  user: string;
  preferredJobRoles: string[];
  experienceLevel: ExperienceLevel;
  languages: string[];
  interviewGoals: string[];
  preferredLocations: string[];
  salaryExpectation: {
    minimum: number;
    maximum: number;
    currency: string;
  };
  workArrangements: string[];
  skillsOfInterest: string[];
  openToOpportunities: boolean;
  createdAt: string;
  updatedAt: string;
  _response?: {
    statusCode: number;
    message: string;
  };
}

// Define a type for the Resume API response
export interface ResumesResponse {
  resumeData: ResumeProtocol[];
  _response?: {
    statusCode: number;
    message: string;
  };
}

// Define a type for the Domain Update API response
export interface DomainUpdate {
  _id: string;
  user: string;
  title: string;
  content: string;
  domain: string;
  tags: string[];
  read: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
}

class CareerService {
  // Add private cache property for api requests
  private preferencesCache: {
    data: CareerPreferencesResponse | null;
    timestamp: number;
    pendingPromise: Promise<CareerPreferencesResponse | null> | null;
  } = {
    data: null,
    timestamp: 0,
    pendingPromise: null
  };

  // Cache TTL in milliseconds (60 seconds)
  private cacheTTL = 60 * 1000;
  // Preferences cache TTL in milliseconds (10 minutes)
  private preferencesCacheTTL = 10 * 60 * 1000;

  // Add private cache property for resume requests
  private resumeCache: {
    data: ResumeProtocol[] | null;
    timestamp: number;
    pendingPromise: Promise<ResumeProtocol[]> | null;
  } = {
    data: null,
    timestamp: 0,
    pendingPromise: null
  };

  // Add private cache property for domain updates requests
  private domainUpdatesCache: {
    data: DomainUpdate[] | null;
    timestamp: number;
    pendingPromise: Promise<DomainUpdate[]> | null;
  } = {
    data: null,
    timestamp: 0,
    pendingPromise: null
  };

  /**
   * Create career preferences for the user
   * @param preferences Career preferences to create
   * @returns Created career preferences
   */
  async createPreferences(preferences: CareerPreferencesCreateRequest): Promise<CareerPreferencesResponse> {
    try {
      // Only send the required fields to the API, not default values
      const requestData = {
        preferredJobRoles: preferences.preferredJobRoles,
        experienceLevelId: preferences.experienceLevelId,
        languageIds: preferences.languageIds,
        interviewGoalIds: preferences.interviewGoalIds
      };
      
      const result = await apiClient.post<CareerPreferencesResponse>('/career-preferences', requestData);
      
      // Remove _response metadata if exists
      const { _response, ...dataWithoutResponse } = result as any;
      
      // Update cache with the created preferences
      this.preferencesCache = {
        data: dataWithoutResponse,
        timestamp: Date.now(),
        pendingPromise: null
      };
        
      return dataWithoutResponse;
    } catch (error: any) {
      console.error('Error creating career preferences:', error);
      // If server says user already has preferences, then call updatePreferences instead
      if (error === 'already exists') {
        return await this.updatePreferences(preferences);
      }
      throw error;
    }
  }

  /**
   * Get career preferences for the current user
   * @param forceRefresh Whether to force a refresh from the API
   * @returns User's career preferences
   */
  async getPreferences(forceRefresh = false): Promise<CareerPreferencesResponse | null> {
    try {
      const now = Date.now();
      const cacheExpired = now - this.preferencesCache.timestamp > this.preferencesCacheTTL;
      
      // If we have a valid cache and don't need to force refresh, return cached data
      if (this.preferencesCache.data && !cacheExpired && !forceRefresh) {
        return this.preferencesCache.data;
      }
      
      // If there's already a pending request and we don't need to force a refresh, return that promise
      if (this.preferencesCache.pendingPromise && !forceRefresh) {
        return this.preferencesCache.pendingPromise;
      }
      
      // Create the fetch promise first before assigning it to the cache
      const fetchPromise = this.fetchPreferencesFromApi().then(result => {
        if (result) {
          // Update cache with the new data
          this.preferencesCache.data = result;
          this.preferencesCache.timestamp = Date.now();
        }
        return result;
      }).finally(() => {
        // Always clear the pending promise when done
        this.preferencesCache.pendingPromise = null;
      });
      
      // Store the promise reference
      this.preferencesCache.pendingPromise = fetchPromise;
      
      // Return the promise
      return fetchPromise;
    } catch (error: any) {
      // Clear pending promise on error
      this.preferencesCache.pendingPromise = null;
      
      // If the error is a 404, it means the user hasn't set preferences yet
      if (error.response && error.response.status === 404) {
        return null;
      }
      
      console.error('Error fetching career preferences:', error);
      throw error;
    }
  }
  
  /**
   * Fetch career preferences directly from the API
   * @private
   * @returns User's career preferences or null if not found
   */
  private async fetchPreferencesFromApi(): Promise<CareerPreferencesResponse | null> {
    try {
      const preferenceData = await apiClient.get<CareerPreferencesResponse>('/career-preferences');
      
      // Check for valid response structure by looking for the _id field
      if (!preferenceData || !preferenceData._id) {
        return null;
      }
      
      // Remove _response metadata if exists
      const { _response, ...dataWithoutResponse } = preferenceData as any;
      
      // Ensure all array fields are initialized even if missing in the API response
      const formattedData: CareerPreferencesResponse = {
        ...dataWithoutResponse,
        preferredJobRoles: Array.isArray(preferenceData.preferredJobRoles) ? preferenceData.preferredJobRoles : [],
        languages: Array.isArray(preferenceData.languages) ? preferenceData.languages : [],
        interviewGoals: Array.isArray(preferenceData.interviewGoals) ? preferenceData.interviewGoals : [],
        preferredLocations: Array.isArray(preferenceData.preferredLocations) ? preferenceData.preferredLocations : [],
        workArrangements: Array.isArray(preferenceData.workArrangements) ? preferenceData.workArrangements : [],
        skillsOfInterest: Array.isArray(preferenceData.skillsOfInterest) ? preferenceData.skillsOfInterest : [],
      };
      
      return formattedData;
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        return null;
      }
      
      throw error;
    }
  }

  /**
   * Update career preferences for the current user
   * @param preferences Career preferences to update
   * @returns Updated career preferences
   */
  async updatePreferences(preferences: CareerPreferencesUpdateRequest): Promise<CareerPreferencesResponse> {
    try {
      // Only send the required fields to the API, not default values
      const requestData = {
        preferredJobRoles: preferences.preferredJobRoles,
        experienceLevelId: preferences.experienceLevelId,
        languageIds: preferences.languageIds,
        interviewGoalIds: preferences.interviewGoalIds
      };
      
      const result = await apiClient.put<CareerPreferencesResponse>('/career-preferences', requestData);
      
      // Remove _response metadata if exists
      const { _response, ...dataWithoutResponse } = result as any;
      
      // Update cache with the updated preferences
      this.preferencesCache = {
        data: dataWithoutResponse,
        timestamp: Date.now(),
        pendingPromise: null
      };
      
      return dataWithoutResponse;
    } catch (error) {
      console.error('Error updating career preferences:', error);
      throw error;
    }
  }
  
  /**
   * Map UI preferences format to API preferences format
   * Since job roles are now stored as strings, we pass them directly
   */
  mapUIPreferencesToAPIFormat(
    uiPreferences: CareerPreferencesUIFormat,
    configData: ConfigDataResponse
  ): CareerPreferencesCreateRequest {
    // Find job role IDs by matching names
    const preferredJobRoles = uiPreferences.jobRoles.map(name => {
      const role = configData.jobRoles.find(r => r.title === name || r.name === name);
      return role ? role._id : name; // Fallback to name if not found, though ideally it should always be found
    });

    // Find experience level by exact name match
    let experienceLevel = configData.experienceLevels.find(
      level => level.name && level.name.toLowerCase() === uiPreferences.experienceLevel.toLowerCase()
    );
    
    if (!experienceLevel) {
      // Try a different matching approach if strict matching fails
      const fuzzyExperienceLevel = configData.experienceLevels.find(
        level => level.name && level.name.toLowerCase().includes(uiPreferences.experienceLevel.toLowerCase())
      );
      
      if (fuzzyExperienceLevel) {
        experienceLevel = fuzzyExperienceLevel;
      }
    }
    
    // Find language IDs by matching names
    const languages = uiPreferences.languages.map(name => {
      const lang = configData.languages.find(l => l.name === name);
      return lang ? lang._id : name; // Fallback to name
    });
      
    // Find interview goal IDs by matching names
    const interviewGoals = uiPreferences.interviewGoals.map(name => {
      const goal = configData.interviewGoals.find(g => g.name === name);
      return goal ? goal._id : name; // Fallback to name
    });
      
    // Create default values for optional fields
    const defaultPreferences = {
      preferredLocations: [],
      salaryExpectation: {
        minimum: 0,
        maximum: 0,
        currency: 'USD'
      },
      workArrangements: [],
      skillsOfInterest: [],
      openToOpportunities: false
    };
    
    // Parse the salary expectation string to an object if available
    let parsedSalaryExpectation;
    if (uiPreferences.salaryExpectation) {
      try {
        // Assuming the salaryExpectation is a JSON string in the UI format
        parsedSalaryExpectation = JSON.parse(uiPreferences.salaryExpectation);
      } catch (e) {
        console.warn('Failed to parse salary expectation string:', e);
        parsedSalaryExpectation = defaultPreferences.salaryExpectation;
      }
    }
    
    return {
      preferredJobRoles,
      experienceLevelId: experienceLevel?._id || '', // Provide a default empty string to satisfy type
      languageIds: languages,
      interviewGoalIds: interviewGoals,
      preferredLocations: uiPreferences.preferredLocations || defaultPreferences.preferredLocations,
      salaryExpectation: parsedSalaryExpectation || defaultPreferences.salaryExpectation,
      workArrangements: uiPreferences.workArrangements || defaultPreferences.workArrangements,
      skillsOfInterest: uiPreferences.skillsOfInterest || defaultPreferences.skillsOfInterest,
      openToOpportunities: uiPreferences.openToOpportunities ?? defaultPreferences.openToOpportunities
    };
  }

  /**
   * Convert API career preferences format to UI format
   * @param apiPreferences The API preferences response
   * @returns Preferences formatted for UI consumption
   */
  convertToUiFormat(apiPreferences: CareerPreferencesResponse, configData: ConfigDataResponse): CareerPreferencesUIFormat {
    // Map job role IDs to names, and keep custom roles as strings
    let jobRoles: string[] = [];
    if (Array.isArray(apiPreferences.preferredJobRoles)) {
      jobRoles = apiPreferences.preferredJobRoles
        .map(roleIdOrName => {
          const role = configData.jobRoles.find(r => r._id === roleIdOrName);
          // If a role is found by ID, return its name. Otherwise, it's a custom role string.
          return role ? (role.title || role.name) : roleIdOrName;
        })
        .filter(name => name && typeof name === 'string') as string[];
    }

    // Extract experience level name
    let experienceLevel = '';
    if (apiPreferences.experienceLevel) {
      if (typeof apiPreferences.experienceLevel === 'string') {
        experienceLevel = apiPreferences.experienceLevel;
      } else if (typeof apiPreferences.experienceLevel === 'object') {
        experienceLevel = apiPreferences.experienceLevel.name || (apiPreferences.experienceLevel as any)?.value || '';
      }
    }

    // Map language IDs to names
    let languages: string[] = [];
    if (Array.isArray(apiPreferences.languages)) {
      languages = apiPreferences.languages
        .map(langId => {
          const lang = configData.languages.find(l => l._id === langId);
          return lang ? lang.name : null;
        })
        .filter(Boolean) as string[];
    }

    // Map interview goal IDs to names
    let interviewGoals: string[] = [];
    if (Array.isArray(apiPreferences.interviewGoals)) {
      interviewGoals = apiPreferences.interviewGoals
        .map(goalId => {
          const goal = configData.interviewGoals.find(g => g._id === goalId);
          return goal ? goal.name : null;
        })
        .filter(Boolean) as string[];
    }

    return {
      jobRoles,
      experienceLevel,
      languages,
      interviewGoals,
      preferredLocations: apiPreferences.preferredLocations || [],
      salaryExpectation: apiPreferences.salaryExpectation 
        ? JSON.stringify(apiPreferences.salaryExpectation) 
        : undefined,
      workArrangements: apiPreferences.workArrangements || [],
      skillsOfInterest: apiPreferences.skillsOfInterest || [],
      openToOpportunities: apiPreferences.openToOpportunities
    };
  }

  /**
   * Convert UI preferences to API format using configuration data
   * @param uiPreferences The UI preferences to convert
   * @param configData The configuration data containing reference objects
   * @returns Preferences formatted for API consumption
   */
  convertToApiFormat(
    uiPreferences: CareerPreferencesUIFormat, 
    configData: {
      jobRoles: JobRole[];
      experienceLevels: ExperienceLevel[];
      languages: Language[];
      interviewGoals: InterviewGoal[];
    }
  ): CareerPreferencesCreateRequest {
    // Reuse existing mapping function
    return this.mapUIPreferencesToAPIFormat(uiPreferences, configData as ConfigDataResponse);
  }

  /**
   * Get resumes for the current user with retry logic
   * @param forceRefresh Whether to force a refresh from the API
   * @param retryCount Number of retry attempts (used internally)
   * @returns User's resumes
   */
  async getResumes(forceRefresh = false, retryCount = 0): Promise<ResumeProtocol[]> {
    try {
      const now = Date.now();
      const cacheExpired = now - this.resumeCache.timestamp > this.preferencesCacheTTL;
      
      // If we have a valid cache and don't need to force refresh, return cached data
      if (this.resumeCache.data && !cacheExpired && !forceRefresh) {
        return this.resumeCache.data;
      }
      
      // If there's already a pending request and we don't need to force a refresh, return that promise
      if (this.resumeCache.pendingPromise && !forceRefresh) {
        return this.resumeCache.pendingPromise;
      }
      
      // Create the fetch promise with retry logic
      const fetchPromise = this.fetchResumesFromApi()
        .then(result => {
          // Update cache with the new data
          this.resumeCache.data = result;
          this.resumeCache.timestamp = Date.now();
          return result;
        })
        .catch(async (error) => {
          // If this is the first attempt and we get an error, retry once
          if (retryCount === 0) {
            console.log('CareerService: First resume fetch failed, retrying once...');
            // Clear cache and pending promise before retry
            this.resumeCache.pendingPromise = null;
            this.clearResumeCache();
            
            // Wait a bit before retrying
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Retry the fetch
            return this.getResumes(true, 1);
          }
          
          // If retry also failed, throw the error
          throw error;
        })
        .finally(() => {
          // Always clear the pending promise when done
          this.resumeCache.pendingPromise = null;
        });
      
      // Store the promise reference
      this.resumeCache.pendingPromise = fetchPromise;
      
      // Return the promise
      return fetchPromise;
    } catch (error: any) {
      // Clear pending promise on error
      this.resumeCache.pendingPromise = null;
      
      // If the error is a 404, it means the user hasn't created any resumes yet
      if (error.response && error.response.status === 404) {
        return [];
      }
      
      console.error('Error fetching resumes:', error);
      throw error;
    }
  }
  
  /**
   * Fetch resumes directly from the API
   * @private
   * @returns User's resumes or empty array if not found
   */
  private async fetchResumesFromApi(): Promise<ResumeProtocol[]> {
    try {
      const responseData = await apiClient.get<any>('/resumes/my-resume');

      // This is the full response object from the server.
      // It can have many shapes. We need to find the resume data inside it.

      let resumeData: any = null;

      // Case 1: The response is the resume array itself.
      if (Array.isArray(responseData)) {
        resumeData = responseData;
      }
      // Case 2: The resume data is in a `data` property.
      else if (responseData && responseData.data) {
        // Case 2a: response.data is the array
        if (Array.isArray(responseData.data)) {
          resumeData = responseData.data;
        }
        // Case 2b: response.data contains resumeData, which could be an object or array
        else if (responseData.data.resumeData) {
            resumeData = responseData.data.resumeData;
        }
      }
      // Case 3: The resume data is in a `resumeData` property at the root.
      else if (responseData && responseData.resumeData) {
        resumeData = responseData.resumeData;
      }

      // Now, resumeData can be an array, a single object, or null/undefined.
      let resumes: ResumeProtocol[] = [];
      if (Array.isArray(resumeData)) {
        resumes = resumeData;
      } else if (resumeData && typeof resumeData === 'object') {
        resumes = [resumeData]; // Wrap single object in an array
      }

      if (resumes.length > 0) {
        return resumes.map((resume: any) => ({
          ...resume,
          _id: resume.id || resume._id // Ensure _id is always present, using id as fallback
        }));
      }

      return [];
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        return [];
      }

      throw error;
    }
  }

  /**
   * Get domain updates for the current user
   * @param forceRefresh Whether to force a refresh from the API
   * @returns Domain updates
   */
  async getDomainUpdates(forceRefresh = false): Promise<DomainUpdate[]> {
    try {
      const now = Date.now();
      const cacheExpired = now - this.domainUpdatesCache.timestamp > this.cacheTTL;
      
      // If we have a valid cache and don't need to force refresh, return cached data
      if (this.domainUpdatesCache.data && !cacheExpired && !forceRefresh) {
        return this.domainUpdatesCache.data;
      }
      
      // If there's already a pending request and we don't need to force a refresh, return that promise
      if (this.domainUpdatesCache.pendingPromise && !forceRefresh) {
        return this.domainUpdatesCache.pendingPromise;
      }
      
      // Create the fetch promise first before assigning it to the cache
      const fetchPromise = this.fetchDomainUpdatesFromApi().then(result => {
        // Update cache with the new data
        this.domainUpdatesCache.data = result;
        this.domainUpdatesCache.timestamp = Date.now();
        return result;
      }).finally(() => {
        // Always clear the pending promise when done
        this.domainUpdatesCache.pendingPromise = null;
      });
      
      // Store the promise reference
      this.domainUpdatesCache.pendingPromise = fetchPromise;
      
      // Return the promise
      return fetchPromise;
    } catch (error: any) {
      // Clear pending promise on error
      this.domainUpdatesCache.pendingPromise = null;
      
      console.error('Error fetching domain updates:', error);
      throw error;
    }
  }
  
  /**
   * Fetch domain updates directly from the API
   * @private
   * @returns Domain updates or empty array if not found
   */
  private async fetchDomainUpdatesFromApi(): Promise<DomainUpdate[]> {
    try {
      // Define a type that represents the possible response structures
      type DomainUpdateResponse = 
        | { data: DomainUpdate[] } 
        | Record<string, any> 
        | DomainUpdate[];

      const responseData = await apiClient.get<DomainUpdateResponse>('/domain-updates');
      
      // Check if the response is directly an array of updates
      if (Array.isArray(responseData)) {
        return responseData;
      }
      
      // Handle response with numeric keys (0, 1, etc.) and _response property
      if (responseData && typeof responseData === 'object') {
        // Filter out the _response property and convert remaining object to array
        const updates: DomainUpdate[] = [];
        Object.keys(responseData).forEach(key => {
          // Skip the _response property
          if (key !== '_response') {
            const update = (responseData as Record<string, any>)[key];
            if (update && typeof update === 'object') {
              updates.push(update as DomainUpdate);
            }
          }
        });
        
        // If we found updates, return them
        if (updates.length > 0) {
          return updates;
        }
      }
      
      // Handle response with data property
      if (responseData && 'data' in responseData && Array.isArray(responseData.data)) {
        return responseData.data;
      }
      
      return [];
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        return [];
      }
      
      throw error;
    }
  }

  /**
   * Mark domain update as read
   * @param updateId Domain update ID
   * @returns Updated domain update or null if not found
   */
  async markDomainUpdateAsRead(updateId: string): Promise<DomainUpdate | null> {
    try {
      const responseData = await apiClient.post<DomainUpdate>(`/domain-updates/${updateId}/read`);
      
      // Update cache if it exists
      if (this.domainUpdatesCache.data) {
        this.domainUpdatesCache.data = this.domainUpdatesCache.data.map(update => {
          if (update._id === updateId) {
            return { ...update, read: true };
          }
          return update;
        });
      }
      
      return responseData;
    } catch (error) {
      console.error('Error marking domain update as read:', error);
      throw error;
    }
  }

  /**
   * Update domain update status
   * @param updateId Domain update ID
   * @param status New status ('completed', 'pending', etc.)
   * @returns Updated domain update or null if not found
   */
  async updateDomainUpdateStatus(updateId: string, status: string): Promise<DomainUpdate | null> {
    try {
      const responseData = await apiClient.patch<DomainUpdate>(`/domain-updates/${updateId}`, { status });
      
      // Update cache if it exists
      if (this.domainUpdatesCache.data) {
        this.domainUpdatesCache.data = this.domainUpdatesCache.data.map(update => {
          if (update._id === updateId) {
            return { ...update, status };
          }
          return update;
        });
      }
      
      return responseData;
    } catch (error) {
      console.error('Error updating domain update status:', error);
      throw error;
    }
  }

  /**
   * Delete domain update
   * @param updateId Domain update ID
   * @returns Whether the deletion was successful
   */
  async deleteDomainUpdate(updateId: string): Promise<boolean> {
    try {
      await apiClient.delete<void>(`/domain-updates/${updateId}`);
      
      // Update cache if it exists
      if (this.domainUpdatesCache.data) {
        this.domainUpdatesCache.data = this.domainUpdatesCache.data.filter(update => update._id !== updateId);
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting domain update:', error);
      throw error;
    }
  }

  /**
   * Clear the resume cache to force fresh data on next fetch
   */
  clearResumeCache(): void {
    this.resumeCache = {
      data: null,
      timestamp: 0,
      pendingPromise: null
    };
    console.log('CareerService: Resume cache cleared');
  }
}

// Create an instance and export it
const careerService = new CareerService();
export default careerService;